"use client";

import * as React from "react";

import { submitLead, getCaptcha, type LeadPayload } from "@/lib/api";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^\+?[0-9]{7,15}$/;

export type LeadFieldErrors = Partial<Record<"name" | "email" | "phone" | "captcha_answer", string>>;

/**
 * Shared validation/captcha/submit logic behind every lead-capture surface
 * (the full LeadForm on signup/demo, and the compact chat widget) -- kept
 * as one hook so the captcha flow and phone/email validation only exist in
 * one place, even though the two surfaces render very different layouts.
 */
function useLeadForm(source: LeadPayload["source"], planInterest?: string) {
  const [status, setStatus] = React.useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<LeadFieldErrors>({});
  const [captcha, setCaptcha] = React.useState<{ token: string; image: string } | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = React.useState("");
  const [captchaLoading, setCaptchaLoading] = React.useState(false);

  const refreshCaptcha = React.useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaAnswer("");
    const result = await getCaptcha();
    setCaptcha(result);
    setCaptchaLoading(false);
  }, []);

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetching the initial captcha on mount, not synchronizing derived state.
    refreshCaptcha();
  }, [refreshCaptcha]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // Captured before the `await` below -- React nulls out
    // event.currentTarget once synchronous event dispatch finishes, so
    // reading it after an async gap (to call .reset()) would throw.
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const name = String(form.get("name") ?? "").trim();
    const email = String(form.get("email") ?? "").trim();
    const phone = String(form.get("phone") ?? "").trim();

    const errors: LeadFieldErrors = {};
    if (!name) errors.name = "Please enter your name.";
    if (!email || !EMAIL_PATTERN.test(email)) errors.email = "Please enter a valid email address.";
    if (!phone || !PHONE_PATTERN.test(phone)) errors.phone = "Enter a valid phone number, digits only (7-15 digits).";
    if (!captcha) errors.captcha_answer = "Loading the verification code -- please wait a moment and try again.";
    else if (!captchaAnswer.trim()) errors.captcha_answer = "Please enter the code shown in the image.";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setStatus("submitting");

    const result = await submitLead({
      name,
      email,
      phone,
      clinic_name: String(form.get("clinic_name") ?? "") || undefined,
      message: String(form.get("message") ?? "") || undefined,
      source,
      plan_interest: planInterest,
      captcha_token: captcha!.token,
      captcha_answer: captchaAnswer.trim(),
    });

    setFeedback(result.message);
    setStatus(result.ok ? "success" : "error");

    if (result.ok) {
      formEl.reset();
    } else {
      // The token is single-use and was just consumed by this attempt
      // (right or wrong) -- always fetch a new one so the next try works.
      await refreshCaptcha();
      if (result.errors) {
        setFieldErrors({
          name: result.errors.name?.[0],
          email: result.errors.email?.[0],
          phone: result.errors.phone?.[0],
          captcha_answer: result.errors.captcha_answer?.[0],
        });
      }
    }
  }

  return {
    status,
    feedback,
    fieldErrors,
    captcha,
    captchaAnswer,
    setCaptchaAnswer,
    captchaLoading,
    refreshCaptcha,
    handleSubmit,
  };
}

export { useLeadForm };
